import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["MENS_OPEN", "WOMENS_OPEN", "MENS_DOUBLES", "WOMENS_DOUBLES", "SPECTATOR"];

export default function AdminPromos({ token }) {
  const [promos, setPromos] = useState(null);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(0);
  const [flat, setFlat] = useState(0);
  const [expires, setExpires] = useState("");
  const [maxUses, setMaxUses] = useState(0);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetch(`${API}/admin/promos`, { headers: { "X-Admin-Token": token } })
      .then((r) => r.json())
      .then((d) => setPromos(d.promos || []))
      .catch(() => toast.error("Could not load promos"));
  };
  useEffect(load, [token]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/promos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({
          code,
          percent: Number(percent) || 0,
          flat_paise: Math.round((Number(flat) || 0) * 100),
          expires_at: expires ? `${expires}T23:59:59+05:30` : "",
          max_uses: Number(maxUses) || 0,
          categories: cats,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Create failed");
      toast.success(`Promo ${data.created} created.`);
      setCode(""); setPercent(0); setFlat(0); setExpires(""); setMaxUses(0); setCats([]);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const label = "font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2";

  return (
    <div className="mt-8 max-w-3xl" data-testid="admin-promos">
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={label}>CODE</label><input className="field" data-testid="ap-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LAUNCH10" /></div>
        <div><label className={label}>% OFF</label><input className="field" data-testid="ap-percent" type="number" value={percent} onChange={(e) => setPercent(e.target.value)} /></div>
        <div><label className={label}>OR FLAT ₹ OFF</label><input className="field" data-testid="ap-flat" type="number" value={flat} onChange={(e) => setFlat(e.target.value)} /></div>
        <div><label className={label}>EXPIRES</label><input className="field" data-testid="ap-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} /></div>
        <div><label className={label}>MAX USES (0 = UNLIMITED)</label><input className="field" data-testid="ap-max" type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} /></div>
        <div>
          <label className={label}>CATEGORIES (EMPTY = ALL)</label>
          <div className="flex flex-wrap gap-3 pt-2">
            {CATS.map((c) => (
              <label key={c} className="font-mono text-[10px] tracking-[0.1em] text-[#a3a39f] flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="accent-[#f0eee4]"
                  checked={cats.includes(c)}
                  onChange={(e) => setCats(e.target.checked ? [...cats, c] : cats.filter((x) => x !== c))}
                  data-testid={`ap-cat-${c.toLowerCase()}`}
                />
                {c.replace(/_/g, " ")}
              </label>
            ))}
          </div>
        </div>
      </div>
      <button onClick={create} disabled={busy || !code.trim()} data-testid="ap-create" className="btn-solid mt-6 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] px-8 py-4 hover:bg-white disabled:opacity-40">
        {busy ? <Loader2 size={14} className="animate-spin inline" /> : "CREATE PROMO"}
      </button>

      <div className="mt-10 border border-[#2e2e2a]" data-testid="ap-list">
        {promos === null ? (
          <p className="px-4 py-6"><Loader2 size={16} className="animate-spin" /></p>
        ) : promos.length === 0 ? (
          <p className="px-4 py-6 font-mono text-[11px] tracking-[0.15em] text-[#6e6e6a]" data-testid="ap-empty">NO PROMO CODES YET.</p>
        ) : (
          promos.map((p) => (
            <div key={p.code} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 border-b border-[#2e2e2a] last:border-0 font-mono text-[11px] tracking-[0.1em]" data-testid={`ap-row-${p.code.toLowerCase()}`}>
              <span className="font-bold">{p.code}</span>
              <span className="text-[#a3a39f]">{p.percent ? `${p.percent}% OFF` : `₹${p.flat_paise / 100} OFF`}</span>
              <span className="text-[#6e6e6a]">USED {p.used_count}{p.max_uses ? `/${p.max_uses}` : ""}</span>
              <span className="text-[#6e6e6a]">{p.expires_at ? `EXPIRES ${p.expires_at.slice(0, 10)}` : "NO EXPIRY"}</span>
              <span className="text-[#6e6e6a]">{p.categories?.length ? p.categories.join(", ") : "ALL CATEGORIES"}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
